// Sugalarm — /api/devices wrappers.

import { request } from "../lib/apiClient";
import type { ApiResult, Device } from "../lib/types";

export interface DeviceListResponse {
  count: number;
  devices: Device[];
}

export interface RegisterIn {
  client_id: string;
  name: string;
  device_type: "kiosk" | "web" | "mobile" | "other";
}

export function list(): Promise<ApiResult<DeviceListResponse>> {
  return request<DeviceListResponse>("/api/devices");
}

export function register(body: RegisterIn): Promise<ApiResult<Device>> {
  return request<Device>("/api/devices/register", { method: "POST", body });
}

export function heartbeat(client_id: string): Promise<ApiResult<Device>> {
  return request<Device>("/api/devices/heartbeat", {
    method: "POST",
    body: { client_id },
  });
}

export interface DevicePatchIn {
  // Cycle 100 — tri-state: omit to leave unchanged, null to unset
  // (agent uses its local default), or a wake-word key to assign.
  wake_word?: string | null;
  // Cycle 377 — display name. Omit to leave unchanged; send a non-empty
  // string (≤100 chars, server-stripped) to rename. NOT nullable: the
  // server 422s an explicit null, since `devices.name` is NOT NULL.
  // Display-only — renaming never touches the device token, so the
  // device stays paired.
  name?: string;
}

export function patchDevice(
  deviceId: string,
  body: DevicePatchIn,
): Promise<ApiResult<Device>> {
  return request<Device>(`/api/devices/${deviceId}`, { method: "PATCH", body });
}

export function revokeToken(deviceId: string): Promise<ApiResult<Device>> {
  return request<Device>(`/api/devices/${deviceId}/revoke_token`, { method: "POST" });
}

export function deleteDevice(deviceId: string): Promise<ApiResult<void>> {
  return request<void>(`/api/devices/${deviceId}`, { method: "DELETE" });
}

// Cycle 131: remove the caller's revoked / stale device rows (keeps every
// device with a live token or recent activity).
export function pruneInactive(): Promise<ApiResult<{ removed: number }>> {
  return request<{ removed: number }>("/api/devices/prune_inactive", {
    method: "POST",
  });
}

// Cycle 200: a device self-reports its hardware capabilities. Operates on
// the CALLING device (resolved server-side from the device-token identity)
// — no device_id, no IDOR. Device-token auth only; a JWT-only caller is
// rejected. The Android tablet kiosk calls this once on mount with
// `{ camera_capable: true }` so the food-photo endpoint's softened auth
// lets its device token through.
export function selfReportCapabilities(body: {
  camera_capable: boolean;
}): Promise<ApiResult<{ camera_capable: boolean }>> {
  return request<{ camera_capable: boolean }>(
    "/api/devices/self/capabilities",
    { method: "POST", body },
  );
}

/** Cycle 458 — the kiosk tells the server a photo chooser opened WITHOUT its
 *  cycle-455 camera purpose reaching the APK (no bridge on this build, or the
 *  post threw). The APK then routes on `capture` alone — the in-app FRONT
 *  camera, even for a meal. Closed enums only; device-token auth; the
 *  calling device is the subject (no id in the body). Counts, never stores.
 *  A purpose that WAS posted but missed the APK's 400 ms pairing window is
 *  invisible to the page; the APK will report it as `timing_miss` itself. */
export type CameraFallbackReason = "no_bridge" | "post_failed";

export function reportCameraFallback(body: {
  purpose: "meal_photo" | "avatar";
  reason: CameraFallbackReason;
}): Promise<ApiResult<null>> {
  return request<null>("/api/devices/self/camera_fallback", {
    method: "POST",
    body,
  });
}

/** Cycle 418 — hand every credential this install holds back to the server.
 *
 * ⚠ NO `user_id` FIELD, BY DESIGN. The caller's account comes from the JWT, so
 * the endpoint is self-scoped by construction and IDOR is unrepresentable.
 * `204` for both "deleted" and "nothing to delete", so it is safely retryable
 * and discloses nothing. */
export interface DeviceReleaseIn {
  push_tokens?: string[];
  live_activity_tokens?: string[];
  web_push_endpoints?: string[];
  /** Cycle 470 — TRUE ONLY ON AN EXPLICIT SIGN-OUT. Asks the server to also
   *  delete every watch push token under the caller's account: the watch
   *  registers its OWN APNs token from the watch app, so this install can
   *  never name it in `push_tokens`. An auth REJECTION sends `false` (and no
   *  push tokens at all) — an expired session is not a sign-out, lows kill.
   *  A boolean, never a string: the server is strict and 422s anything else. */
  release_watch_tokens?: boolean;
}

/**
 * @param bearer - Cycle 470: the access token `teardownAccount` captured
 *   BEFORE `clearAll()`, handed in the way `authApi.logout` takes it. The
 *   release is fired and not awaited, and a `noRetry` ride-along means a stray
 *   401 on a sign-out can never re-enter the refresh dance and drive a second
 *   teardown. Omitted, the call reads the live bearer as before.
 */
/** Cycle 471 — a kiosk's EXPLICIT sign-out revokes its OWN device token and
 *  releases its own push rows. Device-token auth only; the CALLING device is
 *  the subject (no id in the body, so no IDOR). `push_tokens` names what this
 *  install remembers registering; the server also deletes the rows it linked
 *  by `device_id`, so an empty list is a valid body. A second call answers
 *  401 (the credential is gone) and the teardown treats that as done. */
export interface SelfRevokeIn {
  push_tokens?: string[];
}

export interface SelfRevokeOut {
  revoked: boolean;
  push_tokens_released: number;
}

/**
 * @param bearer - the `dt_` device token `teardownAccount` captured BEFORE
 *   `clearAll()`, handed in explicitly the way `authApi.logout` takes the
 *   JWT; `noRetry` keeps a 401 (already revoked) out of the refresh dance.
 */
export function selfRevokeToken(
  body: SelfRevokeIn,
  bearer: string,
): Promise<ApiResult<SelfRevokeOut>> {
  return request<SelfRevokeOut>("/api/devices/self/revoke_token", {
    method: "POST",
    body,
    bearer,
    noRetry: true,
  });
}

export function releaseCredentials(
  body: DeviceReleaseIn,
  bearer?: string | null,
): Promise<ApiResult<void>> {
  // ⚠ NO `JSON.stringify` HERE. `apiClient.request` stringifies `opts.body`
  // itself, so pre-encoding sent a JSON *string literal* and the endpoint
  // 422'd every release — silently, because the caller ignores the result.
  // Every other function in this file passes a raw object; match them.
  return request<void>("/api/devices/release", {
    method: "POST",
    body,
    ...(bearer ? { bearer, noRetry: true } : {}),
  });
}
